import { useMemo, useRef, useState, useEffect } from "react";
import ReactDOM from "react-dom";

// Polyfill for findDOMNode in React 19 (required by react-quill)
// React 19 removed findDOMNode, but react-quill still uses it internally
// @ts-ignore - findDOMNode was removed in React 19 but react-quill needs it
if (typeof window !== "undefined" && !(ReactDOM as any).findDOMNode) {
  (ReactDOM as any).findDOMNode = function (node: any): Element | Text | null {
    if (node == null) {
      return null;
    }
    // If it's already a DOM node, return it
    if (node.nodeType === 1 || node.nodeType === 3) {
      return node;
    }
    // If it's a ref object with current, return the current value
    if (typeof node === "object" && "current" in node) {
      const current = node.current;
      if (current && (current.nodeType === 1 || current.nodeType === 3)) {
        return current;
      }
      // If current is a React component instance, try to find its DOM node
      if (current && typeof current === "object") {
        // Try common patterns for getting DOM nodes from React components
        if (current._reactInternalFiber || current._reactInternalInstance) {
          // For older React versions
          let fiber =
            current._reactInternalFiber || current._reactInternalInstance;
          while (fiber) {
            if (
              fiber.stateNode &&
              (fiber.stateNode.nodeType === 1 || fiber.stateNode.nodeType === 3)
            ) {
              return fiber.stateNode;
            }
            fiber = fiber.return;
          }
        }
      }
    }
    // For React component instances, try to find DOM node via internal structure
    if (typeof node === "object") {
      // Try to access internal React fiber structure
      const fiber =
        (node as any)._reactInternalFiber ||
        (node as any)._reactInternalInstance;
      if (fiber) {
        let currentFiber = fiber;
        while (currentFiber) {
          if (
            currentFiber.stateNode &&
            (currentFiber.stateNode.nodeType === 1 ||
              currentFiber.stateNode.nodeType === 3)
          ) {
            return currentFiber.stateNode;
          }
          currentFiber = currentFiber.return;
        }
      }
    }
    return null;
  };
}

// @ts-ignore - react-quill has CommonJS export structure
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { cn } from "../lib/utils";

export interface RichTextEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  maxLength?: number;
}

export const RichTextEditor = ({
  value = "",
  onChange,
  placeholder = "Enter description...",
  className,
  disabled = false,
  maxLength,
}: RichTextEditorProps) => {
  const quillRef = useRef<ReactQuill>(null);
  const [textLength, setTextLength] = useState(0);
  const [isEditorReady, setIsEditorReady] = useState(false);
  const contentSetRef = useRef<string>(""); // Track which value has been set
  const isSettingContentRef = useRef(false); // Track if we're currently setting content
  const lastUserInputRef = useRef<string>(""); // Track last user input to prevent overwriting

  // Configure Quill modules with full feature set
  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          [{ font: [] }],
          [{ size: [] }],
          ["bold", "italic", "underline", "strike", "blockquote"],
          [
            { list: "ordered" },
            { list: "bullet" },
            { indent: "-1" },
            { indent: "+1" },
          ],
          [{ script: "sub" }, { script: "super" }],
          [{ direction: "rtl" }],
          [{ color: [] }, { background: [] }],
          [{ align: [] }],
          ["link", "image", "video"],
          ["code-block"],
          ["clean"],
        ],
        handlers: {
          image: function () {
            try {
              const quill = quillRef.current?.getEditor();
              if (!quill) return;

              const input = document.createElement("input");
              input.setAttribute("type", "file");
              input.setAttribute("accept", "image/*");
              input.click();

              input.onchange = () => {
                const file = input.files?.[0];
                if (!file) return;

                // Convert image to base64 for embedding
                const reader = new FileReader();
                reader.onload = (e) => {
                  try {
                    const base64 = e.target?.result as string;
                    const range = quill.getSelection(true);
                    if (range) {
                      quill.insertEmbed(range.index, "image", base64, "user");
                    }
                  } catch (error) {
                    console.error("Error inserting image:", error);
                  }
                };
                reader.readAsDataURL(file);
              };
            } catch (error) {
              console.error("Error in image handler:", error);
            }
          },
        },
      },
      clipboard: {
        matchVisual: false,
      },
    }),
    [],
  );

  const formats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "indent",
    "script",
    "direction",
    "color",
    "background",
    "align",
    "link",
    "image",
    "video",
    "code-block",
  ];

  // Helper function to safely get editor instance
  const getEditor = () => {
    if (!isEditorReady || !quillRef.current) {
      return null;
    }
    try {
      return quillRef.current.getEditor();
    } catch (error) {
      return null;
    }
  };

  // Helper function to safely get text content
  const getTextContent = () => {
    try {
      const editor = getEditor();
      return editor?.getText() || "";
    } catch (error) {
      return "";
    }
  };

  useEffect(() => {
    const editor = getEditor();
    const root = editor?.root;
    if (!editor || !root) return;

    const handlePaste = (event: ClipboardEvent) => {
      if (!event.clipboardData) return;

      const html = event.clipboardData.getData("text/html");
      const text = event.clipboardData.getData("text/plain");
      const payload = html || text;

      if (!payload) return;

      // Always insert pasted content as plain text so HTML source stays editable.
      event.preventDefault();
      const range = editor.getSelection(true);
      const insertAt = range?.index ?? editor.getLength();
      editor.insertText(insertAt, payload, "user");
      editor.setSelection(insertAt + payload.length, 0, "silent");
    };

    root.addEventListener("paste", handlePaste);
    return () => {
      root.removeEventListener("paste", handlePaste);
    };
  }, [isEditorReady]);

  const handleChange = (content: string) => {
    // Get text length for character count
    let currentLength = 0;
    let textContent = "";

    try {
      if (quillRef.current) {
        const editor = quillRef.current.getEditor();
        if (editor) {
          // Get plain text content (raw HTML will be visible as text)
          textContent = editor.getText();
          currentLength = textContent.length;
        } else {
          textContent = content;
          currentLength = content.length;
        }
      } else {
        textContent = content;
        currentLength = content.length;
      }
    } catch (error) {
      textContent = content;
      currentLength = content.length;
    }

    // Check max length if provided
    if (maxLength && currentLength > maxLength) {
      return; // Don't update if exceeds max length
    }

    // Update text length state
    setTextLength(currentLength);

    // Mark that we're updating from user input to prevent effect from overwriting
    contentSetRef.current = textContent;
    lastUserInputRef.current = textContent; // Track user's latest input

    // For raw HTML display: use the plain text content directly
    // This preserves HTML tags as visible text
    onChange?.(textContent);
  };

  // Track previous value to detect changes
  const prevValueRef = useRef<string>(value || "");

  // Single effect to handle both initialization and value changes
  // Display raw HTML as plain text - no parsing or conversion
  useEffect(() => {
    const currentValue = value || "";
    const prevValue = prevValueRef.current;

    // Function to set content in editor
    const setContentInEditor = (): boolean => {
      if (!quillRef.current) {
        return false;
      }

      try {
        const editor = quillRef.current.getEditor();
        if (editor && editor.root) {
          setIsEditorReady(true);

          // Get current text from editor
          const currentText = editor.getText();

          // Only update if:
          // 1. Content hasn't been set yet (initial mount) - indicated by contentSetRef mismatch
          // 2. Value prop changed from external source (prevValue !== currentValue)
          //    AND editor content doesn't match the new value
          //    AND we're not overwriting user's unsaved input
          const valueChangedExternally = currentValue !== prevValue;
          const contentNotSet = contentSetRef.current !== currentValue;
          const editorContentMismatch = currentText !== currentValue;

          // Check if editor has user's unsaved input (different from both prop value and last tracked input)
          const hasUnsavedUserInput =
            currentText !== currentValue &&
            currentText !== lastUserInputRef.current &&
            lastUserInputRef.current !== "";

          // Only update if:
          // - External change AND editor doesn't match AND no unsaved user input
          // - Initial setup AND editor doesn't match
          // Never overwrite if user has unsaved input
          const shouldUpdate =
            !hasUnsavedUserInput &&
            ((valueChangedExternally && editorContentMismatch) || // External change and editor doesn't match
              (contentNotSet && editorContentMismatch)); // Initial setup and editor doesn't match

          if (shouldUpdate) {
            isSettingContentRef.current = true;
            editor.setText(currentValue);
            setTextLength(currentValue.length);
            contentSetRef.current = currentValue;
            lastUserInputRef.current = currentValue; // Update last input ref
            // Reset flag after a brief delay
            setTimeout(() => {
              isSettingContentRef.current = false;
            }, 100);
          } else if (currentText === currentValue) {
            // Editor already has the correct content, just update the refs
            contentSetRef.current = currentValue;
            lastUserInputRef.current = currentValue;
          }
          return true; // Success
        }
      } catch (error) {
        // Editor not ready yet
      }
      return false;
    };

    // Update prevValue ref AFTER checking, so we can detect external changes
    const valueChangedExternally = currentValue !== prevValue;
    prevValueRef.current = currentValue;

    // If value hasn't changed externally and content is already set, skip
    if (!valueChangedExternally && contentSetRef.current === currentValue) {
      // Still try once to ensure content is set (in case editor was cleared)
      const timer = setTimeout(() => {
        setContentInEditor();
      }, 100);
      return () => clearTimeout(timer);
    }

    // Try to set content with multiple attempts at different intervals
    const timers: NodeJS.Timeout[] = [];

    // Immediate attempt
    if (setContentInEditor()) {
      // Success on first try, but still set up a delayed check to ensure it sticks
      timers.push(
        setTimeout(() => {
          setContentInEditor();
        }, 200),
      );
    } else {
      // Retry attempts at various intervals if immediate attempt failed
      [50, 100, 200, 300, 500, 800, 1200, 2000].forEach((delay) => {
        timers.push(
          setTimeout(() => {
            setContentInEditor();
          }, delay),
        );
      });
    }

    return () => {
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, [value]); // Re-run when value changes

  return (
    <div className={cn("rich-text-editor-wrapper", className)}>
      <style
        dangerouslySetInnerHTML={{
          __html: `
        .rich-text-editor-wrapper .ql-container {
          font-family: inherit;
          font-size: 14px;
          min-height: 200px;
          border-bottom-left-radius: 0.375rem;
          border-bottom-right-radius: 0.375rem;
        }
        .rich-text-editor-wrapper .ql-editor {
          min-height: 200px;
        }
        .rich-text-editor-wrapper .ql-editor.ql-blank::before {
          color: #9ca3af;
          font-style: normal;
        }
        .rich-text-editor-wrapper .ql-toolbar {
          border-top-left-radius: 0.375rem;
          border-top-right-radius: 0.375rem;
          border: 1px solid #d1d5db;
          border-bottom: none;
        }
        .rich-text-editor-wrapper .ql-container {
          border: 1px solid #d1d5db;
          border-top: none;
        }
        .rich-text-editor-wrapper .ql-toolbar.ql-snow {
          background-color: #f9fafb;
        }
        .rich-text-editor-wrapper .ql-container.ql-snow {
          background-color: white;
        }
        .rich-text-editor-wrapper .ql-editor:focus {
          outline: 2px solid rgb(240, 162, 105);
          outline-offset: -2px;
        }
        .rich-text-editor-wrapper .ql-snow .ql-stroke {
          stroke: #374151;
        }
        .rich-text-editor-wrapper .ql-snow .ql-fill {
          fill: #374151;
        }
        .rich-text-editor-wrapper .ql-snow .ql-picker-label {
          color: #374151;
        }
        .rich-text-editor-wrapper .ql-snow.ql-toolbar button:hover,
        .rich-text-editor-wrapper .ql-snow .ql-toolbar button:hover,
        .rich-text-editor-wrapper .ql-snow.ql-toolbar button.ql-active,
        .rich-text-editor-wrapper .ql-snow .ql-toolbar button.ql-active {
          color: var(--color-brand-primary);
        }
        .rich-text-editor-wrapper .ql-snow.ql-toolbar button:hover .ql-stroke,
        .rich-text-editor-wrapper .ql-snow .ql-toolbar button:hover .ql-stroke,
        .rich-text-editor-wrapper .ql-snow.ql-toolbar button.ql-active .ql-stroke,
        .rich-text-editor-wrapper .ql-snow .ql-toolbar button.ql-active .ql-stroke {
          stroke: var(--color-brand-primary);
        }
        .rich-text-editor-wrapper .ql-snow.ql-toolbar button:hover .ql-fill,
        .rich-text-editor-wrapper .ql-snow .ql-toolbar button:hover .ql-fill,
        .rich-text-editor-wrapper .ql-snow.ql-toolbar button.ql-active .ql-fill,
        .rich-text-editor-wrapper .ql-snow .ql-toolbar button.ql-active .ql-fill {
          fill: var(--color-brand-primary);
        }
      `,
        }}
      />
      <ReactQuill
        ref={quillRef}
        theme="snow"
        // Don't pass value prop - make it uncontrolled to prevent ReactQuill from parsing HTML
        // We'll manage content entirely via setText() in effects
        onChange={(content) => {
          // Only handle change if we're not currently setting content programmatically
          if (!isSettingContentRef.current) {
            handleChange(content);
          }
        }}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        readOnly={disabled}
        preserveWhitespace={true}
        onFocus={() => {
          // Mark editor as ready when it receives focus
          // DO NOT overwrite content on focus - let the effect handle syncing
          // This prevents losing user input when clicking outside and back in
          if (quillRef.current) {
            try {
              const editor = quillRef.current.getEditor();
              if (editor) {
                setIsEditorReady(true);
                // Update text length to reflect current editor content
                const currentText = editor.getText();
                setTextLength(currentText.length);
              }
            } catch (error) {
              // Editor not ready yet
            }
          }
        }}
        onBlur={() => {
          // On blur, sync the editor content to the form if it has changed
          // This ensures user input is saved when they click outside
          if (quillRef.current) {
            try {
              const editor = quillRef.current.getEditor();
              if (editor) {
                const currentText = editor.getText();
                // If editor content differs from prop value, update the form
                if (currentText !== (value || "")) {
                  lastUserInputRef.current = currentText;
                  contentSetRef.current = currentText;
                  onChange?.(currentText);
                }
              }
            } catch (error) {
              // Editor not ready yet
            }
          }
        }}
      />
      {maxLength && (
        <div className="text-xs text-gray-500 mt-1 text-right">
          {textLength} / {maxLength} characters
        </div>
      )}
    </div>
  );
};
